import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import {
  GET_HIGH_REVIEW_SCORE_USERS,
  GET_RECENT_VIDEO_CALL_FEMALE_USERS,
  KORU_GET_USERS,
} from "~/constants/apiEndpoints";
import { APPLICATION_ID } from "~/constants/app";
import { apiClient } from "~/lib/apiClient";
import { queryKeys } from "~/lib/queryKeys";
import type {
  DiscoverUser,
  HighReviewScoreUsersResponse,
  KoruGetUsersResponse,
  KoruUser,
  RecentVideoCallUsersResponse,
} from "./types";

const PAGE_SIZE = 20;

// koru_get_users は keyset ページング（元API meet_people と同方式）。
// 2ページ目以降は前ページ末尾の last_login_time をカーソルとして渡す。
const buildRequestBody = (lastLoginTime: string | undefined) => {
  return {
    application_id: APPLICATION_ID,
    limit: PAGE_SIZE,
    ...(lastLoginTime !== undefined ? { last_login_time: lastLoginTime } : {}),
  };
};

/**
 * koru_get_users のレスポンスを Discover 一覧の DiscoverUser へ変換する。
 * このAPIは一覧カード表示に必要なフィールドのみ返すため、Discover 側でしか使わない
 * フィールド（通話中・ライブ中・ストーリー・フリマ出品等）は既定値で補完する。
 */
const toDiscoverUser = (u: KoruUser): DiscoverUser => ({
  user_id: u.user_id,
  ava_id: u.ava_id,
  abt: u.abt,
  age: u.age,
  user_name: u.user_name,
  region: u.region,
  voice_call_waiting: u.voice_call_waiting,
  video_call_waiting: u.video_call_waiting,
  is_new_user: u.is_new_user,
  last_login_time: u.last_login_time,
  has_lovense: u.has_lovense,
  bust_size: u.bust_size,
  average_score: u.average_score,
  review_count: u.review_count,
  is_calling: false,
  is_live_now: false,
  has_story_movie: false,
  step_to_call: 0,
  is_listed_on_flea_market: false,
});

const RECENT_VIDEO_CALL_LIMIT = 20;

export const recentVideoCallUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.recentVideoCallUsers(),
    queryFn: () =>
      apiClient<RecentVideoCallUsersResponse>(
        GET_RECENT_VIDEO_CALL_FEMALE_USERS,
        {
          application_id: APPLICATION_ID,
          limit: RECENT_VIDEO_CALL_LIMIT,
        },
      ),
    // サーバー側が1時間キャッシュするため、クライアント側も同じ時間を設定
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

const HIGH_REVIEW_SCORE_LIMIT = 20;

export const highReviewScoreUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.highReviewScoreUsers(),
    queryFn: async () => {
      const users = await apiClient<HighReviewScoreUsersResponse>(
        GET_HIGH_REVIEW_SCORE_USERS,
        { limit: HIGH_REVIEW_SCORE_LIMIT },
      );
      // API によって last_login_time / last_login どちらで返るか不定なため正規化
      return users.map((u) => ({
        ...u,
        last_login_time: u.last_login_time || u.last_login || "",
      }));
    },
    // サーバー側が1時間キャッシュするため、クライアント側も同じ時間を設定
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

export const discoverUsersInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: queryKeys.discover.list(),
    queryFn: async ({ pageParam }) => {
      const users = await apiClient<KoruGetUsersResponse>(
        KORU_GET_USERS,
        buildRequestBody(pageParam),
      );
      return users.map(toDiscoverUser);
    },
    initialPageParam: undefined as string | undefined,
    // koru_get_users はサーバー側で除外（ブロック/会話済み/お気に入り）を行うため、続きがあっても
    // 1 ページが PAGE_SIZE 未満で返ることがある。件数で打ち切るとそこで止まるので、
    // カーソル (last_login_time) の有無だけで判定する。空ページ＝最終要素なしで終端。
    getNextPageParam: (lastPage) => lastPage.at(-1)?.last_login_time,
  });
